<template>
  <div class="card-body">
    <div v-if="search" class="filter">
      <InputTypeComponent :model="searchModel" @update="filter" />
    </div>
    <div class="w-100">
      <div v-if="visibleCrews.length">
        <div
          v-for="crew in visibleCrews"
          :key="crew.id"
          :class="{
            crew: visibleCrews.length > 1,
          }"
        >
          <div v-if="visibleCrews.length > 1 && crew.triads && Object.keys(crew.triads).length" class="crew__title">
            {{ crew.title }}
          </div>
          <div class="row" v-if="crew.triads && Object.keys(crew.triads).length">
            <div
              v-for="triad in crew.triads"
              :key="triad.id"
              :class="{
                'col-4': lateralTab,
                'col-xxl-3 col-xl-4 col-lg-6 col-md-4 col-sm-6': !lateralTab,
              }"
            >
              <div
                class="widget apprentice"
                :class="{
                  '-active': this.triad.id && this.triad.id === triad.id,
                  '-incidentSheet': triad.hasIncidentSheet,
                  '-disabled': !isTriadAccessible(triad),
                  '-livretInactive': isLivretInactive(triad),
                }"
                role="button"
                tabindex="0"
                :aria-disabled="!isTriadAccessible(triad)"
                v-tooltip="{
                  title: triadTooltip(triad),
                }"
                @click="activate(triad)"
                @keydown.enter.prevent="activate(triad)"
                @keydown.space.prevent="activate(triad)"
                :style="'border: 2px solid ' + setTriadCardBackgroundColor(triad) + ';'"
              >
                <div class="d-flex align-items-center">
                  <AvatarComponent :disablePicture="true" :user="triad.apprentice" profile="apprentice" />
                  <div class="name">
                    {{ triad.apprentice && triad.apprentice.firstName ? triad.apprentice.firstName : "" }}
                    {{ triad.apprentice && triad.apprentice.lastName ? triad.apprentice.lastName : "" }}
                  </div>
                </div>
                <div
                  v-if="triad.tutor && user.id === triad.tutor.id"
                  v-tooltip="{
                    title: $t('studea_manager.contract.also_tutor'),
                  }"
                  class="tutorIcon"
                >
                  T
                </div>
                <div v-if="isLivretInactive(triad)" class="livretInactiveIcon" aria-hidden="true">
                  <FontAwesomeIcon icon="fa-light fa-book" />
                </div>
                <div
                  v-if="triad.hasIncidentSheet"
                  v-tooltip="{
                    title: $t('studea_manager.contract.incident_sheet'),
                  }"
                  class="incidentSheet"
                >
                  <FontAwesomeIcon icon="fa-light fa-warning" />
                </div>
                <div
                  v-if="
                    (triad.contractRuptureDate && triad.hasBeenValidatedByStudeaManager) ||
                    triad.hasTrainingCourseBeenDroppedOut
                  "
                  class="position-absolute top-1 end-0 pe-2"
                >
                  <FontAwesomeIcon
                    v-tooltip="{
                      title: setTooltipTitle(triad),
                    }"
                    class="text-danger"
                    :icon="'fa-light fa-' + setContractIcon(triad)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="triad__empty">{{ $t("dashboard.no_triad") }}</div>
    </div>
    <BlockLoader :loading="trainingCourse?.loading && lateralTab" />
  </div>
</template>

<script>
import AvatarComponent from "@/components/utilities/AvatarComponent.vue";
import InputTypeComponent from "@/components/form/InputTypeComponent.vue";
import { mapState } from "pinia";
import { useTriadStore } from "@/store/triad/triad";
import { useUserStore } from "@/store/user/user";
import { dateFormatter } from "@/services/utilities/date-formatter";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import BlockLoader from "@/components/blocks/BlockLoader.vue";

export default {
  name: "TriadUsersListComponent",
  components: { BlockLoader, FontAwesomeIcon, InputTypeComponent, AvatarComponent },
  props: {
    lateralTab: {
      type: Boolean,
      required: false,
      default: false,
    },
    search: {
      type: Boolean,
      required: false,
      default: false,
    },
    trainingCourse: {
      type: Object,
      required: true,
    },
  },
  emits: ["dashboard", "triadLoaded"],
  data() {
    return {
      searchModel: {
        vars: {
          name: "searchTriad",
          id: "searchTriad",
          block_prefixes: ["", "text"],
          value: "",
          attr: {
            placeholder: "Filtrer",
          },
        },
      },
      filteredCrews: [],
    };
  },
  computed: {
    ...mapState(useTriadStore, {
      triad: (store) => store.triad,
    }),
    ...mapState(useUserStore, {
      user: (store) => store.user,
    }),
    visibleCrews() {
      return Object.values(this.filteredCrews).filter((crew) => crew?.triads?.length);
    },
  },
  watch: {
    "trainingCourse.crews"() {
      if (this.trainingCourse && this.trainingCourse.crews && this.trainingCourse.crews.length) {
        this.filteredCrews = this.trainingCourse.crews.map((crew) => ({
          ...crew,
          triads:
            crew && crew.triads
              ? crew.triads.filter((triad) => triad.active !== 0).sort((a, b) => b.search - a.search)
              : [],
        }));
      }
    },
  },
  mounted() {
    if (this.trainingCourse?.crews?.length) {
      this.filteredCrews = this.trainingCourse.crews.map((crew) => ({
        ...crew,
        triads:
          crew && crew.triads
            ? crew.triads.filter((triad) => triad.active !== 0).sort((a, b) => b.search - a.search)
            : [],
      }));
    }
  },

  methods: {
    setTriadCardBackgroundColor(triad) {
      if (triad?.activeContract?.contractType?.contractTypeColor) {
        return triad.activeContract.contractType.contractTypeColor;
      }
    },

    isTriadAccessible(triad) {
      return triad.currentUserHasAccess !== false && triad.currentUserHasLivretAccess !== false;
    },

    isLivretInactive(triad) {
      return triad.currentUserHasAccess !== false && triad.currentUserHasLivretAccess === false;
    },

    fullName(user) {
      return user ? [user.firstName, user.lastName].filter(Boolean).join(" ") : "";
    },

    triadTooltip(triad) {
      const apprenticeName = this.fullName(triad.apprentice);
      if (triad.currentUserHasAccess === false) {
        return apprenticeName + "\n" + this.$t("studea_manager.contract.no_current_access");
      }
      if (triad.currentUserHasLivretAccess === false) {
        return apprenticeName + "\n" + this.$t("contract.livret_not_active");
      }
      return (
        apprenticeName +
        "\n" +
        this.$t("Tutor") +
        " : " +
        this.fullName(triad.tutor) +
        "\n" +
        this.$t("Apprentice master") +
        " : " +
        this.fullName(triad.apprenticeMaster)
      );
    },

    activate(triad) {
      if (this.isTriadAccessible(triad)) {
        this.dashboard(triad.id, this.trainingCourse.id);
      }
    },

    dashboard(triadId, trainingCourseId) {
      this.$emit("dashboard");
      useTriadStore()
        .goDashboard(triadId, trainingCourseId)
        .then(() => {
          this.$emit("triadLoaded");
        });
    },

    filter(keyword) {
      keyword = keyword ? keyword.toLowerCase() : "";
      this.filteredCrews = {};
      if (this.trainingCourse?.crews && Object.keys(this.trainingCourse.crews).length) {
        Object.values(this.trainingCourse.crews).forEach((crew) => {
          if (crew && crew.triads && Object.keys(crew.triads).length) {
            const triads = Object.values(crew.triads)
              .filter((triad) => {
                return (
                  triad.active !== 0 &&
                  (triad.apprentice.firstName.toLowerCase().includes(keyword) ||
                    triad.apprentice.lastName.toLowerCase().includes(keyword))
                );
              })
              .sort((a, b) => b.search - a.search);

            this.filteredCrews[crew.id] = {
              id: crew.id,
              title: crew.title,
              triads,
            };
          }
        });
      }
    },

    setTooltipTitle(triad) {
      if (triad.contractRuptureDate) {
        return (
          this.$t("studea_manager.contract.contract_rupture_date") +
          dateFormatter.format(triad.contractRuptureDate, "short")
        );
      } else if (triad.hasTrainingCourseBeenDroppedOut && triad.droppedOutDate) {
        return this.$t("studea_manager.contract.dropped_out") + dateFormatter.format(triad.droppedOutDate, "short");
      }
    },

    setContractIcon(triad) {
      if (triad.contractRuptureDate) {
        return "link-slash";
      } else if (triad.hasTrainingCourseBeenDroppedOut && triad.droppedOutDate) {
        return "triangle-exclamation";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.widget {
  &.apprentice {
    padding: 10px;
    display: inline-flex;
    align-items: center;
    width: 100%;
    cursor: pointer;
    height: 65px;

    &.-incidentSheet {
      padding: 10px 45px 10px 10px;
    }

    @include mobile {
      width: 100%;
      margin-right: 0;
    }

    &.-active {
      border-color: $blue;
    }

    &:hover {
      background: $light-blue;
    }

    &:focus-visible {
      background: $light-blue;
    }

    &.-disabled {
      opacity: 0.5;
      cursor: not-allowed;

      &:hover {
        background: transparent !important;
      }
    }

    &.-livretInactive {
      background: rgba($orange, 0.08);
      border-style: dashed !important;

      @include dark-theme {
        background: rgba($orange, 0.15);
      }
    }

    @include dark-theme {
      color: $white;

      &:hover {
        background: $m-color_9 !important;
      }
    }
  }

  .livretInactiveIcon {
    position: absolute;
    right: 8px;
    bottom: 6px;
    color: $orange;
    font-size: 14px;
  }

  .name {
    margin-left: 5px;
  }

  .card {
    order: 2;

    &.-first {
      order: 1;

      .card-header {
        background: $light-blue;

        @include dark-theme {
          background: $m-color_18;
          color: $white;
        }
      }
    }

    .card-body {
      display: flex;
      flex-wrap: wrap;
    }
  }
}

.filter {
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e0e6ed;
}

.crew {
  border-bottom: 1px dashed $m-color_6;
  margin-bottom: 10px;

  &:last-child {
    border-bottom: none;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--primary-color);
    margin-bottom: 10px;
  }
}

.tutorIcon {
  margin-left: 10px;
  font-size: 12px;
  padding: 1px 5px;
  border-radius: 6px;
  color: $primary;
  background-color: #f5f5fe;
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.incidentSheet {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: $orange;
  margin-left: 10px;
  font-size: 18px;
}

.triad__empty {
  @include dark-theme {
    color: $white;
  }
}
</style>
